import { alpha, lighten, Stack, Typography, useTheme } from "@mui/material";
import { Calculator } from "lucide-react";
import type { FC } from "react";
import {
	monthlyContribution,
	monthsUntil,
} from "web_myplann/Goals/Helpers/monthlyContribution";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	savingsTarget: number;
	dueDate: Date | undefined | null;
	compact: boolean;
};

export const MonthlyProjectionRow: FC<Props> = ({
	savingsTarget,
	dueDate,
	compact,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const months = monthsUntil(dueDate);
	const monthly = monthlyContribution(savingsTarget, dueDate);
	const hasResult = months > 0 && monthly > 0;

	const monthlyUnit = t(Resources.Goals.Form.MonthlyProjection.monthlyUnit);
	const monthlyText = `${monthly.toLocaleString("cs-CZ")} ${monthlyUnit}`;

	const baseSx = {
		px: 2,
		py: 1.5,
		borderTop: `1px solid ${theme.palette.grey[100]}`,
	} as const;

	if (!hasResult) {
		return (
			<Stack
				direction="row"
				alignItems="center"
				gap={0.75}
				sx={{ ...baseSx, bgcolor: theme.palette.grey[50] }}
			>
				<Calculator size={14} color={theme.palette.grey[400]} />
				<Typography fontSize={12} color={theme.palette.grey[400]}>
					{t(Resources.Goals.Form.MonthlyProjection.placeholder)}
				</Typography>
			</Stack>
		);
	}

	if (compact) {
		return (
			<Stack
				direction="row"
				alignItems="center"
				gap={0.75}
				sx={{
					...baseSx,
					bgcolor: alpha(lighten(theme.palette.primary.main, 0.9), 0.3),
				}}
			>
				<Calculator size={14} color={theme.palette.primary.main} />
				<Typography fontSize={12} color={theme.palette.primary.main}>
					{t(Resources.Goals.Form.MonthlyProjection.compactPrefix)}
					{monthlyText} {t(Resources.Goals.Form.MonthlyProjection.atRateInline)}
				</Typography>
			</Stack>
		);
	}

	const years = Math.floor(months / 12);
	const remainderMonths = months % 12;
	const yearsAbbr = t(Resources.Goals.Form.MonthlyProjection.yearsAbbr);
	const monthsAbbr = t(Resources.Goals.Form.MonthlyProjection.monthsAbbr);
	const subtitle = `${t(Resources.Goals.Form.MonthlyProjection.atRateBlock)}, ${years} ${yearsAbbr} ${remainderMonths} ${monthsAbbr}`;

	return (
		<Stack
			sx={{
				...baseSx,
				bgcolor: alpha(lighten(theme.palette.primary.main, 0.9), 0.3),
			}}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Typography
					fontSize={14}
					fontWeight={500}
					color={theme.palette.primary.main}
				>
					{t(Resources.Goals.Form.MonthlyProjection.label)}
				</Typography>
				<Typography
					fontSize={14}
					fontWeight={700}
					color={theme.palette.primary.dark}
					sx={{ fontVariantNumeric: "tabular-nums" }}
				>
					{monthlyText}
				</Typography>
			</Stack>
			<Typography
				fontSize={11}
				color={theme.palette.primary.main}
				sx={{ opacity: 0.7, mt: 0.25, textAlign: "right" }}
			>
				{subtitle}
			</Typography>
		</Stack>
	);
};
